import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  CardHeadeWithActions,
  ConfirmDelete,
  MwButton,
  MwSelector,
  MwSpinnerButton,
  MwToast,
  NoDataFound,
  Pagination,
} from "../components/ui";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { TimeMangementPopup } from "../popups/TimeMangementPopup";
import periodsContext from "../utils/TimeMangeHelper";
import {
  IoIosCheckmark,
  IoIosClose,
  IoIosCloseCircleOutline,
  IoMdClose,
} from "react-icons/io";
import { FaCheck } from "react-icons/fa";
import { LucideCheck } from "lucide-react";
import { toast } from "react-toastify";

const getSelectedValue = (selected) =>
  typeof selected === "string" ? selected : selected?.value || "";

export const TimeMange = () => {
  const [t] = useTranslation("global");
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [actionData, setActionData] = useState({
    mode: null,
    periodId: null,
  });
  const [filter, setFilter] = useState({
    state: "all",
    closeduntil: "all",
  });
  const [isOpenDel, setIsOpenDel] = useState(false);
  const [isOpenClose, setIsOpenClose] = useState(false);
  const [isOpenDef, setIsOpenDef] = useState(false);

  const [id, setid] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isShowToast, setIsShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const emptyPeriodsHandledRef = useRef(false);
  const perPage = 15;

  const {
    periods,
    loading,
    saving,
    deletePeriod,
    fetchPeriods,
    serverPagination,
    fetched,
    closedPeriod,
    defualtPeriod,
  } = useContext(periodsContext);

  const filteredPeriods = useMemo(() => {
    const selectedState = getSelectedValue(filter.state).toLowerCase();
    const selectedClosedUntil = getSelectedValue(
      filter.closeduntil,
    ).toLowerCase();

    return (periods || []).filter((period) => {
      const periodState = String(period.state || "").toLowerCase();
      const periodClosedStatus = period.is_closed === true ? "1" : "0";

      const matchesState =
        !selectedState || selectedState === "all" || periodState === selectedState;
      const matchesClosedStatus =
        !selectedClosedUntil ||
        selectedClosedUntil === "all" ||
        periodClosedStatus === selectedClosedUntil;

      return matchesState && matchesClosedStatus;
    });
  }, [
    periods,
    filter.state,
    filter.closeduntil,
  ]);

  const sortedPeriods = useMemo(() => {
    const toTime = (value) => {
      if (!value) return Number.POSITIVE_INFINITY;
      const timestamp = Date.parse(String(value));
      return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
    };

    return [...filteredPeriods].sort((a, b) => {
      const aTime = toTime(a?.startDateDisplay || a?.fin_start_date);
      const bTime = toTime(b?.startDateDisplay || b?.fin_start_date);
      return aTime - bTime;
    });
  }, [filteredPeriods]);

  const hasAnyData = (periods || []).length > 0;
  const isServerPagination = Boolean(serverPagination.enabled);
  const effectiveCurrentPage = isServerPagination
    ? serverPagination.currentPage
    : currentPage;
  const effectivePerPage = isServerPagination
    ? serverPagination.perPage || perPage
    : perPage;
  const totalPages = isServerPagination
    ? serverPagination.totalPages
    : Math.max(1, Math.ceil(sortedPeriods.length / perPage));
  const startIndex = (effectiveCurrentPage - 1) * effectivePerPage;
  const displayedPeriods = isServerPagination
    ? sortedPeriods
    : sortedPeriods.slice(startIndex, startIndex + perPage);

  useEffect(() => {
    if (!isServerPagination) {
      setCurrentPage(1);
    }
  }, [
    filter.state,
    filter.closeduntil,
    isServerPagination,
  ]);

  useEffect(() => {
    if (!isServerPagination && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, isServerPagination]);

  useEffect(() => {
    if (!fetched) return;
    if (loading) return;

    if ((periods || []).length > 0) {
      emptyPeriodsHandledRef.current = false;
      return;
    }

    if (emptyPeriodsHandledRef.current) return;

    emptyPeriodsHandledRef.current = true;
    setActionData({ mode: "ADD", periodId: null });
    setIsOpen(true);
    setToastMsg(
      location.state?.periodToastMessage || t("times.noPeriodsRedirect"),
    );
    setIsShowToast(true);

    if (location.state?.openAddPeriod || location.state?.periodToastMessage) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [loading, periods, location.pathname, location.state, navigate, t]);

  const handlePageChange = async (page) => {
    if (page < 1 || page > totalPages) return;

    if (isServerPagination) {
      await fetchPeriods({
        page,
        perPage: serverPagination.perPage,
      });
      return;
    }

    setCurrentPage(page);
  };

  const handleDelete = async (id) => {
    try {
      await deletePeriod(id);
      setIsOpenDel(false);
    } catch {}
  };
  const handleClosePeriod = async (id) => {
    try {
      await closedPeriod(id);
      setIsOpenClose(false);
    } catch (e) {
      console.error(
        "Failed to close period",
        e?.response?.data || e?.message || e,
      );
      setToastMsg(
        e?.response?.data?.message || e?.message || t("times.closePeriodError"),
      );
      setIsShowToast(true);
    }
  };
  const handleDefualtPeriod = async (id) => {
    try {
      await defualtPeriod(id);
      setIsOpenDef(false);
    } catch (e) {
      console.error(
        "Failed to set default period",
        e?.response?.data || e?.message || e,
      );
    }
  };
  return (
    <section className="relative p-2 font-normal">
      <MwToast msg={toastMsg} isShow={isShowToast} setIsShow={setIsShowToast} />
      <CardHeadeWithActions
        wImage={"w-7"}
        title={t("menu.timeMange")}
        icon={"/assets/icons/time.png"}
      >
        <MwButton
          onClick={() => {
            setIsOpen(true);
            setActionData({ mode: "ADD", periodId: null });
          }}
          classNameCustom="w-max !font-normal text-white bg-[#261759] hover:bg-[#463977]"
          children={t("common.addtime")}
          type="newBtn"
        />
      </CardHeadeWithActions>

      {hasAnyData && (
        <div className="FILTERS grid grid-cols-6 gap-3">
          <MwSelector
            _data={[
              {
                id: "0",
                label: t("common.all"),
                value: "all",
                lang: i18next.language,
              },
              {
                id: "1",
                label: t("times.active"),
                value: "active",
                lang: i18next.language,
              },
              {
                id: "2",
                label: t("times.inactive"),
                value: "inactive",
                lang: i18next.language,
              },
            ]}
            withAddNew={false}
            withSearch={false}
            label={t("times.status")}
            dataType="labelValue"
            classnameSelect="font-normal"
            selectedItem={filter.state}
            setSelectedItem={(e) =>
              setFilter((prev) => ({ ...prev, state: e }))
            }
          />
          <MwSelector
            _data={[
              {
                id: "0",
                label: t("common.all"),
                value: "all",
                lang: i18next.language,
              },
              {
                id: "1",
                label: t("times.opened"),
                value: "0",
                lang: i18next.language,
              },
              {
                id: "2",
                label: t("times.closed"),
                value: "1",
                lang: i18next.language,
              },
            ]}
            withAddNew={false}
            withSearch={false}
            label={t("times.closedStatus")}
            dataType="labelValue"
            classnameSelect="font-normal"
            selectedItem={filter.closeduntil}
            setSelectedItem={(e) =>
              setFilter((prev) => ({ ...prev, closeduntil: e }))
            }
          />
        </div>
      )}

      <div className="flex justify-center items-start w-full h-full">
        {loading ? (
          <div className="w-full h-[20vh] flex justify-center items-center">
            <MwSpinnerButton />
          </div>
        ) : displayedPeriods.length > 0 ? (
          <table className="w-full mt-3">
            <thead className="bg-[#ebf9d9]">
              <tr className="text-center text-sm text-[#261759]">
                <th className="p-2 font-normal">{t("times.number")}</th>
                <th className="p-2 font-normal">{t("times.startPeriod")}</th>
                <th className="p-2 font-normal">{t("times.endPeriod")}</th>
                <th className="p-2 font-normal">{t("times.closeUntil")}</th>
                <th className="p-2 font-normal">{t("times.status")}</th>
                <th className="p-2 font-normal">{t("times.actions")}</th>
              </tr>
            </thead>
            <tbody className="font-normal">
              {displayedPeriods.map((period, index) => {
                const isCurrent =
                  period.is_current === 1 || period.is_current === true;
                const isClosed =
                  period.closeduntil === "closed" || period.is_closed;
                const isInactive = period.state === "inactive";

                return (
                  <tr
                    key={period.id}
                    className="hover:bg-slate-200 text-sm font-normal border-b transition-all"
                  >
                    <td className="p-2 text-center text-[#261759]">
                      {startIndex + index + 1}
                    </td>

                    <td className="p-2 text-center text-[#261759]">
                      {period.startDateDisplay || "-"}
                    </td>

                    <td className="p-2 text-center text-[#261759]">
                      {period.endDateDisplay || "-"}
                    </td>

                    <td className="p-2 text-center text-[#261759]">
                      {period.closedUntilDateDisplay ||
                        (isClosed ? t("times.closed") : t("times.opened"))}
                    </td>

                    <td className="p-2 text-center text-[#261759]">
                      {period.state === "active"
                        ? t("times.active")
                        : t("times.inactive")}
                    </td>

                    <td className="p-2 text-center text-[#261759] flex justify-center items-center gap-2">
                      {/* Edit */}
                      <button
                        onClick={() => {
                          setActionData({ mode: "EDIT", periodId: period.id });
                          setIsOpen(true);
                        }}
                        disabled={ isClosed || isCurrent || !isInactive}
                        className={`transition ${
                          isClosed || isCurrent || !isInactive
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <img
                          src="/assets/icons/Edit.png"
                          alt=""
                          className="w-[12.5px]"
                        />
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => {
                          setid(period.id);
                          setIsOpenDel(true);
                        }}
                        disabled={isCurrent || !isInactive || isClosed}
                        className={`transition ${ isClosed || isCurrent || !isInactive
                         ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <img
                          src="/assets/icons/Trash.png"
                          alt=""
                          className="w-[12.5px]"
                        />
                      </button>

                      {/* Default Button */}
                      <MwButton
                        type="secondary"
                        onClick={() => {
                          setid(period.id);
                          setIsOpenDef(true);
                        }}
                        disabled={isCurrent || isClosed}
                        classNameCustom={`!px-2 flex justify-center items-center gap-2 !h-6 !py-0 text-[11px] transition ${
                          isClosed
                            ? "opacity-50 cursor-not-allowed !text-gray-400"
                            : isCurrent
                              ? "bg-[#4CAF50] !text-white cursor-not-allowed"
                              : "!text-[#261759] hover:bg-[#463977] hover:!text-white"
                        }`}
                      >
                        {t("common.defualt")}

                        <span
                          className={`rounded-full w-3 h-3 flex justify-center items-center ${
                            isInactive
                              ? "bg-gray-200"
                              : isCurrent
                                ? "bg-white"
                                : "bg-[#e4e8f8]"
                          }`}
                        >
                          <FaCheck
                            className={`text-[7px] ${
                              isClosed
                                ? "text-gray-400"
                                : isCurrent
                                  ? "text-[#4CAF50]"
                                  : "text-[#261759]"
                            }`}
                          />
                        </span>
                      </MwButton>

                      {/* Close Button */}
                      <MwButton
                        type="secondary"
                        onClick={() => {
                          setid(period.id);
                          setIsOpenClose(true);
                        }}
                        disabled={isClosed || isCurrent}
                        classNameCustom={`!px-2 flex justify-center items-center gap-2 !h-6 !py-0 text-[11px] transition ${
                          isClosed || isCurrent
                            ? "opacity-50 cursor-not-allowed !text-gray-400"
                            : "!text-[#261759] hover:bg-[#463977] hover:!text-white"
                        }`}
                      >
                        {t("common.closePeriod")}

                        <span
                          className={`rounded-full w-3 h-3 flex justify-center items-center ${
                            isClosed ? "bg-gray-200" : "bg-[#e4e8f8]"
                          }`}
                        >
                          <IoMdClose
                            className={`text-[10px] ${
                              isClosed ? "text-gray-400" : "text-[#261759]"
                            }`}
                          />
                        </span>
                      </MwButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="w-[50%] mt-32 flex justify-center items-center">
            <NoDataFound msg={t("common.noData")} />
          </div>
        )}
      </div>

      <TimeMangementPopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onClose={() => setIsOpen(false)}
        actionData={actionData}
      />
      <ConfirmDelete
        onShow={isOpenDel}
        type="delete"
        msg={t("common.deletemsg")}
        setOnShow={setIsOpenDel}
        onDeleteHandler={() => handleDelete(id)}
        loading={saving}
        onCancelHandle={() => setIsOpenDel(false)}
      />
      <ConfirmDelete
        onShow={isOpenDef}
        type="defualt"
        btnLabel={t("common.defualt")}
        msg={t("common.defualtmsg")}
        setOnShow={setIsOpenDef}
        onDeleteHandler={() => handleDefualtPeriod(id)}
        loading={saving}
        onCancelHandle={() => setIsOpenDef(false)}
      />
      <ConfirmDelete
        onShow={isOpenClose}
        type="close"
        btnLabel={t("common.closePeriod")}
        msg={t("common.closePeriodmsg")}
        setOnShow={setIsOpenClose}
        onDeleteHandler={() => handleClosePeriod(id)}
        loading={saving}
        onCancelHandle={() => setIsOpenClose(false)}
      />
      <Pagination
        currentPage={effectiveCurrentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </section>
  );
};
